import { mutationTypes } from './storeFactories/helpers';
import { storeConstants } from '@/utils/constants';
import { storeUtils } from '@/utils';
import { LogService } from '@/services';
import notificationUtils from '@/utils/notificationUtils';

const defaultApi = {
    api: {
        get: '', // function from api
    },
};

/**
 *
 * @param {string} storeName
 * @param {Object} api
 * @param {Object} config
 *  - actionGetOptionsDefault - options that will be put on the get action - default { refresh: true, cache: false }
 *  - mapListItemFn - function to remap the items
 * @param {Object} overrides
 *  - getOptions - default options for get Action
 *  - state - overrides default state
 *  - getters - overrides getters
 *  - mutations - overrides the mutations
 *  - actions - overrides the actions
 */
const createModuleView = (storeName, api = defaultApi, options = {}, overrides = {}) => {
    const defaultState = {
        list: [],
        totalCount: 0,
        timestamp: 0,
        loading: 0,
        allItemsLoaded: false,
        refresh: false,
        detail: {},
        totals: null,
    };

    let actionGetOptionsDefault =
            options && options.actionGetOptionsDefault
                ? options.actionGetOptionsDefault
                : {
                      refresh: true,
                      offset: 0,
                      limit: storeConstants.ITEMS_PER_PAGE,
                  },
        mapListItemFn = options && options.mapListItemFn ? options.mapListItemFn : false,
        normalizeDetailFn =
            options && options.normalizeDetailFn ? options.normalizeDetailFn : false;

    const module = {
        name: storeName,
        namespaced: true,
        state: { ...defaultState, ...overrides.state },
        getters: {
            isLoading: (state) => !!state.loading,
            allItemsLoaded: (state) => state.allItemsLoaded,
        },
        mutations: {
            [mutationTypes.APPEND_TO_LIST](state, pageData) {
                state.timestamp = new Date().getTime();
                state.list = [
                    ...state.list,
                    ...(mapListItemFn ? pageData.map(mapListItemFn) : pageData),
                ];
            },
            [mutationTypes.CLEAR_LIST](state) {
                state.list = [];
            },
            [mutationTypes.CLEAR_DATA](state) {
                state.list = [];
                state.totalCount = 0;
                state.loading = 0;
                state.allItemsLoaded = false;
                state.refresh = false;
                state.detail = {};
                state.totals = null;
                state.filters = {};
            },
            [mutationTypes.SET_LOADING](state, loading) {
                state.loading = loading;
            },
            [mutationTypes.SET_TOTAL_COUNT](state, totalCount) {
                state.totalCount = totalCount;
            },
            [mutationTypes.SET_REFRESH](state, refresh) {
                state.refresh = refresh;
            },
            [mutationTypes.SET_ALL_ITEMS_LOADED](state, allItemsLoaded) {
                state.allItemsLoaded = allItemsLoaded;
            },
            [mutationTypes.SET_DETAIL](state, detail) {
                state.detail = normalizeDetailFn ? normalizeDetailFn(detail) : detail;
            },
            setTotals(state, totals) {
                state.totals = totals;
            },
        },
        actions: {
            get: async (
                { commit, dispatch, state },
                options = actionGetOptionsDefault,
                customErrorMessage
            ) => {
                const needsRefresh =
                    options.refresh ||
                    state.refresh ||
                    storeUtils.isStoreOutDated(state.timestamp, storeConstants.REFRESH_TIME);

                if (needsRefresh) {
                    commit(mutationTypes.CLEAR_LIST);
                    commit(mutationTypes.SET_TOTAL_COUNT, 0);
                    commit(mutationTypes.SET_ALL_ITEMS_LOADED, false);
                }

                commit(mutationTypes.SET_LOADING, state.loading + 1);

                const response = await api.get({
                    offset: options.offset,
                    limit: options.limit,
                    filter: options.filter,
                    sort: options.sort,
                });

                if (response.success) {
                    let { list, totalCount, totals } = response.data;

                    if (list.length === totalCount) {
                        commit(mutationTypes.SET_ALL_ITEMS_LOADED, true);
                    }

                    if (totals && options.offset === 0) {
                        commit('setTotals', totals);
                    }

                    if (needsRefresh) {
                        commit(mutationTypes.CLEAR_LIST);
                    }
                    commit(mutationTypes.SET_TOTAL_COUNT, totalCount);
                    commit(mutationTypes.APPEND_TO_LIST, list);
                    commit(mutationTypes.SET_LOADING, state.loading - 1);

                    return response.data;
                } else {
                    commit(mutationTypes.SET_LOADING, state.loading - 1);
                    commit(mutationTypes.SET_ALL_ITEMS_LOADED, true);
                    dispatch(
                        'notification/error',
                        notificationUtils.getFromApiResponse(response, customErrorMessage),
                        { root: true }
                    );
                    LogService.debug(`problem loading ${storeName}`);
                    return false;
                }
            },
            async getRaw(context, options = {}) {
                const response = await api.get({
                    offset: options.offset,
                    limit: options.limit + 1,
                    filter: options.filter,
                    sort: options.sort,
                });

                if (response.success) {
                    return {
                        ...response,
                        data: {
                            ...response.data,
                            list: response.data.list.map((item) =>
                                mapListItemFn ? mapListItemFn(item) : item
                            ),
                        },
                    };
                } else {
                    return response;
                }
            },
            async one({ commit, dispatch }, { id, customErrorMessage }) {
                const response = await api.one(id);
                commit(mutationTypes.SET_DETAIL, response);

                if (response.success) {
                    commit(mutationTypes.SET_DETAIL, response.data);
                    return response.data;
                } else {
                    dispatch(
                        'notification/error',
                        notificationUtils.getFromApiResponse(response, customErrorMessage),
                        { root: true }
                    );
                    LogService.debug(`problems loading detail on ${storeName}, id: ${id}`);
                    return false;
                }
            },

            clearList({ commit }) {
                commit(mutationTypes.CLEAR_LIST);
            },
            clear({ commit }) {
                commit(mutationTypes.CLEAR_DATA);
            },
            setRefreshIfOutdated({ commit, state }, timestamp) {
                if (timestamp > state.timestamp) {
                    commit(mutationTypes.SET_REFRESH, true);
                }
            },
            setRefresh({ commit }, refresh) {
                commit(mutationTypes.SET_REFRESH, refresh);
            },
        },
    };

    !overrides.getters || Object.assign(module.getters, overrides.getters);
    !overrides.mutations || Object.assign(module.mutations, overrides.mutations);
    !overrides.actions || Object.assign(module.actions, overrides.actions);
    !overrides.modules || (module.modules = overrides.modules);

    return module;
};

export default {
    mutationTypes,
    createModuleView,
};
